/* eslint-disable no-nested-ternary */
import React, { useMemo, useEffect, useRef, useState } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    TextField,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    Button,
    Stack,
    useMediaQuery,
    useTheme,
    Container,
} from "@mui/material";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, getFirestore, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { db } from '../../../../firebase.config';
import CustomButton from "../../../../components/CustomButton";
import { UserAuth } from "../../../../context/AuthContext";
import CustomConfirmationModal from "../../../../components/customModal/confirmModal";
import CustomModal from "../../../../components/customModal/customModal";
import CustomDateTimePicker from "../../../../components/CustomDateTimePicker";

/**
 * Interface Campagne Push — Material UI + react-hook-form (JS)
 * Version: fixed & responsive (aperçu à droite en position: fixed sur ≥ md)
 * - Colonne gauche : attributs (nom, catégorie, template, cible + variables dynamiques)
 * - Colonne droite : aperçu FIXED avec calcul automatique de la largeur et du left
 * - Sur mobile (xs/sm) : l'aperçu redevient statique (stacked)
 * - Rendu moustache minimal ({{var}}) + compteur de caractères
 * - Hook onSubmit prêt à brancher sur Firestore
 */

// AppBar offsets (ajuste selon ton layout)
const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 70;

const CATEGORIES = [
    { id: "promotion", label: "Promotion & Vente" },
    { id: "reminder", label: "Rappels & Logistique" },
    { id: "reengagement", label: "Relance & Réactivation" },
    { id: "transactional", label: "Transactionnel" },
];

const TEMPLATES = {
    promotion: [
        {
            id: "promo_flash",
            name: "Promo limitée",
            body:
                "🚨 Offre spéciale ! -{{discount}}% sur {{eventName}} jusqu’au {{endDate}}. 👉 {{deeplink}}",
            variables: {
                discount: { type: "number", required: true },
                eventName: { type: "string", required: true },
                endDate: { type: "date", required: true },
                deeplink: { type: "url", required: true },
            },
            maxLength: 180,
        },
        {
            id: "last_tickets",
            name: "Derniers billets",
            body:
                "⚡ Il ne reste que {{remainingTickets}} billets pour {{eventName}} ! Réserve vite 👉 {{deeplink}}",
            variables: {
                remainingTickets: { type: "number", required: true },
                eventName: { type: "string", required: true },
                deeplink: { type: "url", required: true },
            },
            maxLength: 180,
        },
    ],
    reminder: [
        {
            id: "reminder_j1",
            name: "Rappel J-1",
            body:
                "👋 {{firstName}}, {{eventName}} c’est demain à {{eventTime}} ({{eventLocation}}). Tes billets : {{deeplink}}",
            variables: {
                firstName: { type: "string", required: false, fallback: "ami" },
                eventName: { type: "string", required: true },
                eventTime: { type: "time", required: true },
                eventLocation: { type: "string", required: true },
                deeplink: { type: "url", required: true },
            },
            maxLength: 180,
        },
    ],
    reengagement: [
        {
            id: "abandoned_cart",
            name: "Panier abandonné",
            body:
                "🛒 Tu n’as pas finalisé ton achat pour {{eventName}}. Termine ta commande 👉 {{deeplink}}",
            variables: {
                eventName: { type: "string", required: true },
                deeplink: { type: "url", required: true },
            },
            maxLength: 180,
        },
    ],
    transactional: [
        {
            id: "purchase_confirmed",
            name: "Achat confirmé",
            body: "✅ Ton billet pour {{eventName}} est prêt. 👉 {{deeplink}}",
            variables: {
                eventName: { type: "string", required: true },
                deeplink: { type: "url", required: true },
            },
            maxLength: 180,
        },
    ],
};

const TARGETS = [
    { id: "all", label: "Tous les contacts" },
    { id: "population", label: "Population (segment)" },
    { id: "contacts", label: "Sélection de contacts" },
];

// --- helpers ---
const formatLength = (s, max = 180) => `${(s || "").length}/${max}`;
function renderMustache(tpl, values) {
    if (!tpl) return "";
    let out = "";
    for (let i = 0; i < tpl.length;) {
        const start = tpl.indexOf("{{", i);
        if (start === -1) { out += tpl.slice(i); break; }
        out += tpl.slice(i, start);
        const end = tpl.indexOf("}}", start + 2);
        if (end === -1) { out += tpl.slice(start); break; }
        const key = tpl.slice(start + 2, end).trim();
        const val = values && values[key] != null ? String(values[key]) : "";
        out += val;
        i = end + 2;
    }
    return out;
}

function VariableFields({ control, selectedTemplate }) {
    if (!selectedTemplate) return null;
    const entries = Object.entries(selectedTemplate.variables || {});
    return (
        <Stack spacing={2}>
            {entries.map(([key, spec]) => (
                <Controller
                    key={key}
                    name={`vars.${key}`}
                    control={control}
                    rules={{ required: !!spec.required }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            label={spec.required ? `${key} *` : key}
                            type={
                                spec.type === "number"
                                    ? "number"
                                    : spec.type === "date"
                                        ? "date"
                                        : spec.type === "time"
                                            ? "time"
                                            : spec.type === "url"
                                                ? "url"
                                                : "text"
                            }
                            InputLabelProps={
                                spec.type === "date" || spec.type === "time"
                                    ? { shrink: true }
                                    : undefined
                            }
                            error={!!fieldState.error}
                            helperText={fieldState.error ? "Champ requis" : ""}
                            fullWidth
                            size="small"
                        />
                    )}
                />
            ))}
            <Typography variant="body2" color="text.secondary">
                Astuce : les variables remplacent les <code>{"{{var}}"}</code> dans le message.
            </Typography>
        </Stack>
    );
}

export default function CampaignPushBuilderMUI() {
    const { user, userData } = UserAuth();
    const ORG_PUSH_PATH = (orgId) => `organisations/${orgId}/campagnesPush`;
    const { campaignID } = useParams();
    const navigate = useNavigate();
    // gère /:id ou /:campaignID

    const [loadingCampaign, setLoadingCampaign] = useState(false);
    const [saving, setSaving] = useState(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [openPlanDialog, setOpenPlanDialog] = useState(false);
    const [openReturnDialog, setOpenReturnDialog] = useState(false);

    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    const defaultCategory = CATEGORIES[0].id;
    const defaultTemplateId = TEMPLATES[defaultCategory][0].id;

    const { control, handleSubmit, setValue, reset } = useForm({
        defaultValues: {
            name: "",
            category: defaultCategory,
            templateId: defaultTemplateId,
            target: TARGETS[0].id,
            vars: {},
        },
    });

    const category = useWatch({ control, name: "category" });
    const templateId = useWatch({ control, name: "templateId" });
    const name = useWatch({ control, name: "name" });
    const vars = useWatch({ control, name: "vars" });

    const containerRef = useRef(null);
    const rightColRef = useRef(null);
    const [fixedDims, setFixedDims] = useState({ left: 0, width: 0 });

    const templatesForCat = useMemo(() => TEMPLATES[category] || [], [category]);
    const selectedTemplate = useMemo(
        () => templatesForCat.find((t) => t.id === templateId) || templatesForCat[0],
        [templatesForCat, templateId]
    );

    // plan campagne
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState('');

    // Initialiser/refixer les valeurs des variables à chaque changement de template
    useEffect(() => {
        if (!selectedTemplate) return;
        const entries = Object.entries(selectedTemplate.variables || {});
        entries.forEach(([k, spec]) => {
            const current = vars?.[k];
            if (current == null || current === "") {
                setValue(`vars.${k}`, spec.fallback ?? "");
            }
        });
    }, [selectedTemplate?.id]);

    const preview = useMemo(
        () => renderMustache(selectedTemplate?.body || "", vars),
        [selectedTemplate, vars]
    );
    const maxLen = selectedTemplate?.maxLength ?? 180;

    // Calcule position/largeur du panneau fixed en fonction de la colonne droite
    useEffect(() => {
        if (!isMdUp) return; // pas de fixed sur mobile
        const col = rightColRef.current;
        if (!col) return;

        const compute = () => {
            const rect = col.getBoundingClientRect();
            setFixedDims({ left: rect.left + window.scrollX, width: rect.width });
        };

        compute();

        const ro = new ResizeObserver(() => compute());
        ro.observe(col);
        window.addEventListener('resize', compute);
        window.addEventListener('scroll', compute, { passive: true });

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', compute);
            window.removeEventListener('scroll', compute);
        };
    }, [isMdUp]);

    const launchCampaign = async () => {
        if (!userData?.idOrg) return alert("Organisation introuvable");
        setSaving(true);
        try {
            // 1) S’assurer que la campagne core est bien sauvée
            //    on va relire le formulaire directement
            const values = {
                name,
                category,
                templateId,
                vars,
            };
            await onSubmit(values); // sauvegarde brouillon/maj core

            // 2) Déterminer l’ID du core (si tu utilises l’ID route pour core: ok)
            const coreId = campaignID || /* si tu génères ailleurs, remonte-le ici */ null;
            if (!coreId) {
                return alert("Impossible de trouver l'ID de la campagne core après sauvegarde.");
            }

            // 3) Créer/MàJ le lien org
            const linkRef = doc(db, `${ORG_PUSH_PATH(userData.idOrg)}`, coreId);
            const linkPayload = {
                campaignId: coreId,
                status: 'scheduled',
                // sendAt: schedule.kind === 'scheduled' && schedule.sendAt ? schedule.sendAt : new Date().toISOString(),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };
            const linkSnap = await getDoc(linkRef);
            if (linkSnap.exists()) {
                await updateDoc(linkRef, linkPayload);
            } else {
                await setDoc(linkRef, linkPayload);
            }

            alert("Campagne planifiée ✅");
        } catch (e) {
            console.error(e);
            alert("Erreur lors du lancement");
        } finally {
            setSaving(false);
        }
    };


    const onSubmit = async (data) => {
        setOpenConfirmDialog(false);
        setSaving(true);
        try {
            const coreRef = campaignID
                ? doc(db, "campagnes", campaignID) // si tu réutilises l’ID comme ID core (sinon génère à part)
                : doc(db, "campagnes", crypto.randomUUID());

            const payload = {
                title: data.name,
                category: data.category,
                templateId: data.templateId,
                // on stocke TOUT pour le back:
                /* audience: {
                    mode: audience.mode,
                    segmentIds: audience.segmentIds,
                    includeFollowers: audience.includeFollowers,
                }, */
                overrides: data.vars,
                // (optionnel) si tu veux stocker les tpl résolus pour le back
                updatedAt: serverTimestamp(),
            };

            const coreSnap = await getDoc(coreRef);
            if (coreSnap.exists()) {
                await updateDoc(coreRef, payload);
            } else {
                await setDoc(coreRef, { ...payload, status: "draft", createdAt: serverTimestamp() });
            }

            alert("Brouillon de campagne sauvegardé ✅");
        } catch (e) {
            console.error(e);
            alert("Erreur lors de l'enregistrement");
        } finally {
            setSaving(false);
        }
    };




    useEffect(() => {
        const load = async () => {
            if (!campaignID) return;
            setLoadingCampaign(true);
            try {
                const ref = doc(db, `organisations/${userData.idOrg}/crm/${userData.idOrg}/pushCampains`, campaignID);
                const snap = await getDoc(ref);
                if (snap.exists()) {
                    const d = snap.data();
                    const ref = doc(db, `campagnes`, d.campagnDetailsId);
                    const snap1 = await getDoc(ref);
                    if (snap1.exists()) {
                        const d1 = snap1.data();
                        reset({
                            name: d1.name || "",
                            category: d1.category || defaultCategory,
                            templateId: d1.templateId || defaultTemplateId,
                            target: d1.audience || TARGETS[0].id,
                            vars: d1.overrides || {},
                        });
                    }

                }
            } catch (e) {
                console.log(e)
            } finally {
                setLoadingCampaign(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignID, userData]);


    const topOffset = (isMdUp ? APP_BAR_DESKTOP : APP_BAR_MOBILE) + 30; // ajuste si navbar masquée

    return (
        <Box sx={{ bgcolor: "white", p: 3, height: '100%' }} ref={containerRef}>
            <Grid container spacing={3} sx={{ maxWidth: 1200, mx: "auto" }} alignItems="flex-start">
                {/* Colonne gauche */}
                <Grid item xs={12} md={7.5} sx={{ alignSelf: 'flex-start' }}>
                    <Paper elevation={0} sx={{ p: 3, border: '2px solid gainsboro', borderRadius: '7px',  mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Attributs de la campagne</Typography>

                        <Stack spacing={3}>
                            {/* Nom */}
                            <Controller
                                name="name"
                                control={control}
                                rules={{ required: true }}
                                render={({ field, fieldState }) => (
                                    <Stack spacing={1}>
                                        <Typography variant="body1">Nom de la campagne</Typography>
                                        <TextField {...field} placeholder="Ex: Promo rentrée — Soirée Afro" fullWidth size="medium" error={!!fieldState.error} helperText={fieldState.error ? "Champ requis" : ""} />
                                    </Stack>
                                )}
                            />

                            {/* Catégorie */}
                            <Controller
                                name="category"
                                control={control}
                                render={({ field }) => (
                                    <Stack spacing={1}>
                                        <Typography variant="body1">But / Catégorie</Typography>
                                        <Select {...field} onChange={(e) => {
                                            const cat = e.target.value;
                                            field.onChange(cat);
                                            const firstTpl = (TEMPLATES[cat] || [])[0];
                                            setValue("templateId", firstTpl ? firstTpl.id : "");
                                        }}>
                                            {CATEGORIES.map((c) => (
                                                <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </Stack>

                                )}
                            />

                            {/* Template */}
                            <Controller
                                name="templateId"
                                control={control}
                                render={({ field }) => (
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Template du but sélectionné</InputLabel>
                                        <Select {...field} label="Template du but sélectionné">
                                            {templatesForCat.map((t) => (
                                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />

                            {/* Cible */}
                            <Controller
                                name="target"
                                control={control}
                                render={({ field }) => (
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Cible</InputLabel>
                                        <Select {...field} label="Cible">
                                            {TARGETS.map((t) => (
                                                <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Stack>
                    </Paper>

                    {selectedTemplate && (
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, boxShadow: "0 10px 30px rgba(20,20,20,0.06)" }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Variables dynamiques</Typography>
                            <VariableFields control={control} selectedTemplate={selectedTemplate} />
                        </Paper>
                    )}

                    <Box sx={{ height: 120 }} />
                </Grid>

                {/* Colonne droite (Aperçu FIXED ≥ md / statique < md) */}
                <Grid item xs={12} md={4.5} sx={{ alignSelf: 'flex-start' }}>
                    <Box ref={rightColRef} sx={{ position: 'relative', minHeight: 8 }}>
                        <Box
                            sx={{
                                position: isMdUp ? 'fixed' : 'static',
                                top: isMdUp ? topOffset : 'auto',
                                left: isMdUp ? `${fixedDims.left}px` : 'auto',
                                width: isMdUp ? `${fixedDims.width}px` : 'auto',
                                zIndex: 1100,
                            }}
                        >
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, boxShadow: "0 10px 30px rgba(20,20,20,0.06)", mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>Aperçu notification</Typography>
                                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#fff", boxShadow: "0 10px 30px rgba(20,20,20,0.06)" }}>
                                    <Typography fontWeight={600} sx={{ mb: 0.5 }}>{name || "Titre de la campagne"}</Typography>
                                    <Typography sx={{ whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                                        {preview || "Votre message de notification s'affichera ici."}
                                    </Typography>
                                </Box>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                                    <Typography variant="caption">Longueur : {formatLength(preview, maxLen)}</Typography>
                                    <Typography variant="caption" color="text.secondary">Max {maxLen} caractères</Typography>
                                </Stack>
                            </Paper>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <Container>
                <Stack
                    direction='row'
                    spacing={2}
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        padding: '16px',
                        // borderTop: '2px solid gainsboro',
                        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',

                    }}
                >
                    <CustomButton
                        type='back'
                        buttonText='retour'
                        loading={saving}
                        onClick={() => {
                            setOpenReturnDialog(true);
                        }}
                    />
                    <Stack direction='row' spacing={1}>

                        <CustomButton
                            type='secondaire'
                            buttonText='Planifier'
                            loading={saving}
                            onClick={() => {
                                setOpenPlanDialog(true);
                            }}
                        />
                        <CustomButton
                            type='primaire'
                            buttonText={saving ? "Enregistrement..." : "Valider & Continuer"}
                            loading={saving}
                            onClick={() => {
                                handleSubmit(setOpenConfirmDialog(true));
                            }}
                        />

                    </Stack>

                </Stack>
                <CustomConfirmationModal open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)} onConfirm={handleSubmit(onSubmit)} description={'Vous allez proceder au laucement de votre camapagne push. souhaitez vous confirmer ?'} />
                <CustomConfirmationModal
                    open={openReturnDialog}
                    onClose={() => {
                        setOpenReturnDialog(false)
                        navigate('/marketing/notifs', { replace: true })
                    }}
                    onConfirm={() => {
                        setOpenReturnDialog(false);
                        // saveCampaign(true);
                    }}
                    title="Quitter l'editeur de campagne"
                    loading={saving}
                    description="Vous êtes sur le point de quitter l'editeur de campagne et perdre vos dernieres modifications"
                    confirmText="Enregistrer et quitter"
                    cancelText="Quitter"
                />
                <CustomModal open={openPlanDialog} title={'Quand souhaitez-vous lancher la campagne ?'} description={'Saisisez le jour et l\'heure à laquelle vous souhaité  envoyer votre campagne'} onConfirm={() => {
                    setOpenPlanDialog(false);
                    launchCampaign();
                }} onClose={() => setOpenPlanDialog(false)}>
                    <CustomDateTimePicker date={selectedDate}
                        time={selectedTime}
                        onDateChange={setSelectedDate}
                        onTimeChange={setSelectedTime}
                        errorDate={!selectedDate}
                        errorHour={!selectedTime} />
                </CustomModal>
            </Container>
        </Box>
    );
}
